import { useRef, useState } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Alert, Button, ButtonGroup, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';
import SvgTrash from 'components/svg/SvgTrash';
import { useModal } from 'context/ModalContext';
import { getButtonsCancelOk, getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import FileItem from './FileItem';
import FilesList from './FilesList';
import FolderBreadcrumbs from './FolderBreadcrumbs';
import FolderItem from './FolderItem';
import FoldersList from './FoldersList';
import { isPreviewImage, splitFileName } from './helper/filesUtils';
import type { FilesViewMode } from './model/filesView';
import useFilesContainer from './model/useFilesContainer';
import useFilesContainerDnd from './model/useFilesContainerDnd';
import { isCloudFolder } from './model/folderTree';

interface DlgButton {
    title: string;
    variant: string;
    handleClick?: () => void;
}

interface FilesContainerProps {
    files: LeadCloudFileType[];
    scope: CloudUploadScope;
    imageColProps?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
    };
    canEdit?: boolean;
    addButtonLabel: string;
    uploadingLabel: string;
    saveFirstMessage: string;
    errorMessage: string;
    uploadSuccessMessage: string;
    onFilesChange: (nextFiles: LeadCloudFileType[]) => Promise<boolean>;
    /** Якщо сутність ще не збережена — створити/зберегти і повернути оновлений scope */
    onEnsureEntity?: () => Promise<CloudUploadScope | false>;
    /** Показувати зображення як посилання, без прев’ю */
    noShowPreviewImages?: boolean;
    /** Без папок: немає хлібних крихт, створення папок і навігації */
    noFolder?: boolean;
}

const FilesContainer = ({
    files,
    scope,
    imageColProps,
    canEdit,
    addButtonLabel,
    uploadingLabel,
    saveFirstMessage,
    errorMessage,
    uploadSuccessMessage,
    onFilesChange,
    onEnsureEntity,
    noShowPreviewImages,
    noFolder = false,
}: FilesContainerProps) => {
    const { t } = useTranslation(['FileNavigator', 'common']);
    const { showDlg, hideDlg } = useModal();
    const refInputNewFolder = useRef<HTMLInputElement>(null);
    const refFormNewFolder = useRef<HTMLFormElement>(null);
    const refInputRename = useRef<HTMLInputElement>(null);
    const refFormRename = useRef<HTMLFormElement>(null);
    const [view, setView] = useState<FilesViewMode>('grid');

    const {
        galleryId,
        progress,
        deletingKey,
        renamingKey,
        movingKey,
        isBusy,
        currentPath,
        currentFolders,
        currentFiles,
        setCurrentPath,
        enterFolder,
        refInputFile,
        handleChangeFile,
        openFileInput,
        handleDeleteFile,
        createFolder,
        handleDeleteFolder,
        renameItem,
        moveItem,
    } = useFilesContainer({
        files,
        scope,
        saveFirstMessage,
        errorMessage,
        uploadSuccessMessage,
        folderCreateSuccessMessage: t('messFolderCreate'),
        folderDuplicateMessage: t('messFolderDuplicate'),
        folderInvalidNameMessage: t('messFolderInvalidName'),
        renameSuccessMessage: t('messRenamed'),
        moveSuccessMessage: t('messMovedSuccess'),
        onFilesChange,
        onEnsureEntity,
        noFolder,
    });

    const dndEnabled = Boolean(canEdit) && !noFolder;
    const canDrag = dndEnabled && !isBusy && !deletingKey && !renamingKey && !movingKey;
    const dnd = useFilesContainerDnd({ enabled: canDrag, onMove: moveItem });

    const isEmpty = !currentFolders.length && !currentFiles.length;

    const openCreateFolderDlg = () => {
        if (isBusy) return;

        const submitCreate = (event?: { preventDefault: () => void }) => {
            event?.preventDefault();
            const name = String(refInputNewFolder.current?.value || '').trim();
            hideDlg();
            if (name) void createFolder(name);
        };

        const buttons = [...getButtonsCancelOk(t)] as DlgButton[];
        buttons[1].handleClick = () => submitCreate();

        showDlg({
            title: t('popupCreateFolder'),
            type: 'confirm',
            buttons,
            refFirstFocus: refInputNewFolder,
            text: (
                <form ref={refFormNewFolder} onSubmit={submitCreate}>
                    <div className="mb-0">
                        <label className="form-label">{t('newFolderName')}</label>
                        <input type="text" ref={refInputNewFolder} defaultValue="" className="form-control" required />
                        <small className="form-text text-muted">
                            {t('createIn')}: {currentPath || t('rootFolder')}
                        </small>
                    </div>
                </form>
            ),
        });
    };

    const openRenameDlg = (item: LeadCloudFileType) => {
        if (isBusy) return;

        const isFolder = isCloudFolder(item);
        const { base, ext } = isFolder ? { base: item.fileName, ext: '' } : splitFileName(item.fileName);

        const submitRename = (event?: { preventDefault: () => void }) => {
            event?.preventDefault();
            const name = String(refInputRename.current?.value || '').trim();
            hideDlg();
            if (name) void renameItem(item, name);
        };

        const buttons = [...getButtonsCancelOk(t)] as DlgButton[];
        buttons[1].handleClick = () => submitRename();

        showDlg({
            title: t('oneFileRename'),
            type: 'confirm',
            buttons,
            refFirstFocus: refInputRename,
            text: (
                <form ref={refFormRename} onSubmit={submitRename}>
                    <div className="mb-0">
                        <label className="form-label">{t('renameNewName')}</label>
                        {ext ? (
                            <div className="input-group">
                                <input
                                    type="text"
                                    ref={refInputRename}
                                    defaultValue={base}
                                    className="form-control"
                                    required
                                />
                                <span className="input-group-text text-muted">{ext}</span>
                            </div>
                        ) : (
                            <input
                                type="text"
                                ref={refInputRename}
                                defaultValue={base}
                                className="form-control"
                                required
                            />
                        )}
                    </div>
                </form>
            ),
        });
    };

    const confirmDeleteFolder = (folder: LeadCloudFileType) => {
        const buttons = [...getButtonsNoYesDanger(t)] as DlgButton[];
        buttons[1].handleClick = () => {
            showDlg(false);
            void handleDeleteFolder(folder);
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('popupDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{folder.fileName}</h5>
                    <small className="form-text text-muted">{t('confirmDeleteFolder', { name: folder.fileName })}</small>
                </>
            ),
        });
    };

    return (
        <div className={`file-manger-wrapper file-manger-view-${view} text-start`}>
            <input type="file" ref={refInputFile} onChange={handleChangeFile} className="d-none" multiple />

            {canEdit ? (
                <div className="d-flex gap-1 flex-wrap justify-content-end mb-3">
                    {!noFolder ? (
                        <Button
                            size="sm"
                            variant="light-secondary"
                            disabled={isBusy}
                            onClick={openCreateFolderDlg}
                            title={t('popupCreateFolder')}
                        >
                            <SolarIcon name="folder2-open" className="fs-14 me-1" />
                            {t('popupCreateFolder')}
                        </Button>
                    ) : null}
                    <Button
                        size="sm"
                        variant="light-primary"
                        disabled={isBusy}
                        onClick={() => void openFileInput()}
                        title={addButtonLabel}
                    >
                        {spinnerTitle(
                            <>
                                <SolarIcon name="plus-circle" className="fs-14 me-1" />
                                {addButtonLabel}
                            </>,
                            isBusy,
                            uploadingLabel,
                        )}
                    </Button>
                </div>
            ) : null}

            {progress !== false ? (
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">{uploadingLabel}</small>
                        <small className="text-muted fw-medium">{progress}%</small>
                    </div>
                    <ProgressBar now={Number(progress)} variant="primary" animated style={{ height: 4 }} />
                </div>
            ) : null}

            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <h6 className="mb-0">{t('titleFileList')}</h6>
                <ButtonGroup size="sm" className="file-view-toggle">
                    <Button
                        type="button"
                        size="sm"
                        variant="light-secondary"
                        className={view === 'grid' ? 'active' : ''}
                        title={t('viewIcons')}
                        onClick={() => setView('grid')}
                    >
                        <SolarIcon name="columns-gap" className="fs-12" />
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="light-secondary"
                        className={view === 'list' ? 'active' : ''}
                        title={t('viewList')}
                        onClick={() => setView('list')}
                    >
                        <SolarIcon name="list-ul" className="fs-12" />
                    </Button>
                </ButtonGroup>
            </div>

            <DndContext
                sensors={dnd.sensors}
                collisionDetection={dnd.collisionDetection}
                onDragStart={dnd.handleDragStart}
                onDragEnd={dnd.handleDragEnd}
                onDragCancel={dnd.handleDragCancel}
            >
                {!noFolder ? (
                    <FolderBreadcrumbs currentPath={currentPath} onNavigate={setCurrentPath} droppable={canDrag} />
                ) : null}

                {!noFolder ? (
                    <FoldersList
                        folders={currentFolders}
                        view={view}
                        canDelete={canEdit}
                        deletingKey={deletingKey}
                        renamingKey={renamingKey}
                        movingKey={movingKey}
                        canDrag={canDrag}
                        onOpen={enterFolder}
                        onDelete={confirmDeleteFolder}
                        onRename={openRenameDlg}
                    />
                ) : null}

                <FilesList
                    files={currentFiles}
                    view={view}
                    galleryId={galleryId}
                    imageColProps={imageColProps}
                    canDelete={canEdit}
                    deletingKey={deletingKey}
                    renamingKey={renamingKey}
                    movingKey={movingKey}
                    canDrag={canDrag}
                    onDelete={handleDeleteFile}
                    onRename={openRenameDlg}
                    noShowPreviewImages={noShowPreviewImages}
                />

                <DragOverlay dropAnimation={null}>
                    {dnd.activeItem ? (
                        isCloudFolder(dnd.activeItem) ? (
                            <FolderItem
                                folder={dnd.activeItem}
                                onOpen={() => undefined}
                                overlay
                            />
                        ) : (
                            <FileItem
                                file={dnd.activeItem}
                                galleryId={galleryId}
                                showPreview={!noShowPreviewImages && isPreviewImage(dnd.activeItem.fileName)}
                                overlay
                            />
                        )
                    ) : null}
                </DragOverlay>
            </DndContext>

            {isEmpty ? <Alert variant="warning">{t('emptyFolder')}</Alert> : null}
        </div>
    );
};

export default FilesContainer;
